import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Loader2, DollarSign, TrendingUp, MapPin, FileText,
  Camera, Shield, BarChart3, Building2, Users, Clock,
  ChevronDown, ChevronUp, AlertTriangle, Percent, Download
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTERNAL_ROLES = new Set(["admin", "originations", "underwriter", "processor", "lender_relations", "investor_relations"]);

const LOAN_LABELS = {
  fix_flip: "Fix & Flip", bridge: "Bridge Loan", dscr: "DSCR Rental",
  cash_out: "Cash-Out Refinance", construction: "Construction", ground_up: "Ground Up Construction",
};
const PROP_LABELS = {
  single_family: "Single Family", multi_family: "Multi-Family", condo: "Condo/Townhome",
  commercial: "Commercial", mixed_use: "Mixed Use", land: "Land",
};

function fmtUSD(val) {
  if (!val && val !== 0) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(val);
}

function MetricCard({ label, value, icon: Icon, accent = "emerald" }) {
  const colors = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[accent] || colors.emerald}`} data-testid={`inv-metric-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className="h-4 w-4 opacity-60" />}
        <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</span>
      </div>
      <div className="text-lg font-bold">{value}</div>
    </div>
  );
}

function Section({ title, icon: Icon, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-[#e8e0d0] bg-white overflow-hidden" data-testid={`inv-section-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-3.5 bg-[#faf7f2] hover:bg-[#f5f0e8] transition-colors"
      >
        <div className="flex items-center gap-2.5">
          {Icon && <Icon className="h-4 w-4 text-emerald-700" />}
          <span className="text-sm font-bold text-[#222]">{title}</span>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-[#999]" /> : <ChevronDown className="h-4 w-4 text-[#999]" />}
      </button>
      {open && <div className="px-5 py-4">{children}</div>}
    </div>
  );
}

function FundingBar({ raised, target }) {
  const pct = target > 0 ? Math.min(100, (raised / target) * 100) : 0;
  return (
    <div data-testid="inv-funding-bar">
      <div className="flex justify-between text-xs mb-1.5">
        <span className="font-bold text-emerald-700">{fmtUSD(raised)} raised</span>
        <span className="text-[#999]">{pct.toFixed(0)}% of {fmtUSD(target)}</span>
      </div>
      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function InvestmentPackage({ oppId, oppData, onNavigate }) {
  const [aiSummary, setAiSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  const { user: authUser } = useAuth();
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };
  const isInternal = authUser && INTERNAL_ROLES.has(authUser.role);

  const overview = oppData?.overview || {};
  const deal = oppData?.deal || {};
  const commitments = oppData?.commitments || [];
  const documents = oppData?.documents || [];
  const extra = deal.extra || {};

  const targetRaise = overview.target_raise || 0;
  const raised = overview.raised || 0;
  const minInvestment = overview.min_investment || 0;
  const projectedYield = overview.projected_yield || null;
  const projectedTerm = overview.projected_term_months || null;
  const purchasePrice = deal.purchase_price || 0;
  const arv = deal.arv || extra.after_repair_value || 0;
  const loanAmount = deal.loan_amount || deal.requested_loan_amount || 0;
  const rehabBudget = extra.rehab_budget || 0;
  const arvLtv = arv > 0 && loanAmount > 0 ? ((loanAmount / arv) * 100).toFixed(1) : null;

  // Fetch AI investment summary
  const fetchAISummary = useCallback(async () => {
    setLoadingSummary(true);
    setSummaryError(null);
    try {
      const res = await axios.get(`${API}/entity/opportunity/${oppId}/investment-summary`, { headers });
      setAiSummary(res.data.summary);
    } catch {
      setSummaryError("AI summary unavailable");
    } finally {
      setLoadingSummary(false);
    }
  }, [oppId]);

  useEffect(() => {
    if (oppId) fetchAISummary();
  }, [oppId, fetchAISummary]);

  return (
    <div className="space-y-4" data-testid="investment-package">
      {/* Header */}
      <div className="rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-[#faf7f2] p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200">
                INVESTMENT PACKAGE
              </span>
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${overview.status === "open" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>
                {overview.status === "open" ? "OPEN FOR COMMITMENTS" : (overview.status || "—").toUpperCase()}
              </span>
            </div>
            <h2 className="text-xl font-bold text-[#222]">
              {LOAN_LABELS[deal.loan_type] || deal.loan_type || "Investment"} — {deal.state || "—"}
            </h2>
            <p className="text-sm text-[#666] mt-1">{PROP_LABELS[deal.property_type] || deal.property_type || "Property"}</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-emerald-700">{fmtUSD(targetRaise)}</div>
            <div className="text-[10px] text-[#999]">Target Raise</div>
          </div>
        </div>
      </div>

      {/* Funding Progress */}
      <div className="rounded-xl border border-[#e8e0d0] bg-white p-4">
        <FundingBar raised={raised} target={targetRaise} />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
          <div className="text-center">
            <div className="text-sm font-bold text-[#222]">{commitments.length}</div>
            <div className="text-[10px] text-[#999]">Investors</div>
          </div>
          <div className="text-center">
            <div className="text-sm font-bold text-[#222]">{fmtUSD(minInvestment)}</div>
            <div className="text-[10px] text-[#999]">Min Investment</div>
          </div>
          <div className="text-center">
            <div className="text-sm font-bold text-emerald-600">{fmtUSD(Math.max(0, targetRaise - raised))}</div>
            <div className="text-[10px] text-[#999]">Remaining</div>
          </div>
        </div>
      </div>

      {/* AI Executive Summary */}
      <Section title="Investment Summary" icon={BarChart3}>
        {loadingSummary ? (
          <div className="flex items-center gap-2 py-4">
            <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
            <span className="text-sm text-[#666]">Generating investment analysis...</span>
          </div>
        ) : aiSummary ? (
          <p className="text-sm text-[#333] leading-relaxed whitespace-pre-wrap" data-testid="inv-ai-summary">{aiSummary}</p>
        ) : summaryError ? (
          <div className="flex items-center gap-2 text-sm text-amber-600">
            <AlertTriangle className="h-4 w-4" />
            <span>{summaryError}</span>
            <button onClick={fetchAISummary} className="text-emerald-600 underline text-xs ml-2">Retry</button>
          </div>
        ) : null}
      </Section>

      {/* Investment Terms */}
      <Section title="Investment Terms" icon={DollarSign}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard label="Target Raise" value={fmtUSD(targetRaise)} icon={DollarSign} accent="emerald" />
          <MetricCard label="Min Investment" value={fmtUSD(minInvestment)} icon={DollarSign} accent="blue" />
          {projectedYield && <MetricCard label="Projected Yield" value={`${projectedYield}%`} icon={Percent} accent="violet" />}
          {projectedTerm && <MetricCard label="Term" value={`${projectedTerm} mo`} icon={Clock} accent="amber" />}
        </div>
      </Section>

      {/* Underlying Deal */}
      <Section title="Underlying Asset" icon={Building2}>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-3">
            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#999]">Property Type</span>
              <p className="text-sm font-medium text-[#222]">{PROP_LABELS[deal.property_type] || deal.property_type || "—"}</p>
            </div>
            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#999]">Location</span>
              <p className="text-sm font-medium text-[#222]">{deal.state || "—"}</p>
              <p className="text-[10px] text-[#999] italic mt-0.5">Full address available after commitment</p>
            </div>
            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#999]">Loan Type</span>
              <p className="text-sm font-medium text-[#222]">{LOAN_LABELS[deal.loan_type] || deal.loan_type || "—"}</p>
            </div>
          </div>
          <div className="space-y-3">
            {purchasePrice > 0 && (
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#999]">Purchase Price</span>
                <p className="text-sm font-medium text-[#222]">{fmtUSD(purchasePrice)}</p>
              </div>
            )}
            {arv > 0 && (
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#999]">After Repair Value</span>
                <p className="text-sm font-medium text-[#222]">{fmtUSD(arv)}</p>
              </div>
            )}
            {arvLtv && (
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#999]">ARV LTV</span>
                <p className="text-sm font-medium text-[#222]">{arvLtv}%</p>
              </div>
            )}
          </div>
        </div>
      </Section>

      {/* Borrower Profile — Always anonymized for investors */}
      <Section title="Borrower Profile" icon={Shield}>
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-[#faf7f2] border border-[#e8e0d0]">
            <Shield className="h-5 w-5 text-emerald-600" />
            <div>
              <p className="text-sm font-bold text-[#222]">Identity Protected</p>
              <p className="text-[10px] text-[#999]">Borrower identity is managed by Imaani Capital</p>
            </div>
          </div>
          {extra.borrower_experience && (
            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#999]">Experience Level</span>
              <p className="text-sm text-[#222]">{extra.borrower_experience.replace(/_/g, " ")}</p>
            </div>
          )}
          {extra.credit_score_range && (
            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#999]">Credit Score Range</span>
              <p className="text-sm text-[#222]">{extra.credit_score_range}</p>
            </div>
          )}
        </div>
      </Section>

      {/* Supporting Documents */}
      {documents.length > 0 && (
        <Section title={`Supporting Documents (${documents.length})`} icon={FileText} defaultOpen={false}>
          <div className="space-y-2">
            {documents.map((d, i) => (
              <div key={i} className="flex items-center gap-3 rounded-lg border border-[#e8e0d0] p-3 hover:bg-[#faf7f2] transition-colors">
                <FileText className="h-4 w-4 text-emerald-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#222] truncate">{d.original_filename || "Document"}</p>
                  <p className="text-[10px] text-[#999]">{d.doc_type || "General"}</p>
                </div>
                {(d.url || d.file_url) && (
                  <a href={d.url || d.file_url} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700">
                    <Download className="h-4 w-4" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Footer */}
      <div className="rounded-xl border border-[#e8e0d0] bg-[#faf7f2] p-4 text-center">
        <p className="text-[10px] text-[#999]">
          Investment Package by Imaani Capital &middot; {new Date().toLocaleDateString()}
        </p>
      </div>
    </div>
  );
}
